import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import {ATTRIBUTES, SKILLS, localized} from '../data/traits';
import {DISCIPLINES_BY_ID, sortDisciplineEntries} from '../data/disciplines';
import {buildPool, bloodSurgeBonus, splitPool, frenzyPoolSize, MAX_WILLPOWER_REROLL} from '../dice';
import {getRemaining, getWillpowerMax} from '../character';
import {getBaseParts, getRollLabel, getSpecialtiesFor, isRollImpaired, rollFromRecord} from '../rolls';
import {vtmT, currentLanguage} from '../i18n';
import {VtmPoolResult, PowerRouseLines} from './VtmDice';
import VtmBloodSurgeVeins from './VtmBloodSurgeVeins';
import {useHungerEffectsEnabled} from '../hungerEffects';
import '../../styles/vtm/roll.css';

Modal.setAppElement('#root');

// ACTIVATE : pouvoir sans jet, seulement ses tests d'exaltation
export const ROLL_MODES = {POOL: 'pool', FRENZY: 'frenzy', ACTIVATE: 'activate'};

const parseDifficulty = (value) => (value === '' ? undefined : Math.max(0, parseInt(value, 10) || 0));

const Field = ({label, children, htmlFor}) => (
  <div className="vtmField">
    <label className="vtmFieldLabel" htmlFor={htmlFor}>{label}</label>
    {children}
  </div>
);

const Toggle = ({id, checked, onChange, children}) => (
  <label className="vtmToggle" htmlFor={id}>
    <input id={id} type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />
    <span>{children}</span>
  </label>
);

/**
 * Fenêtre de jet : construction de la réserve puis résultat (avec relance de Volonté).
 * @param {String[]} props.initialTraits [attribut, compétence/attribut/discipline] pré-remplis depuis la fiche
 * @param {Object} [props.power] pouvoir lancé depuis la fiche : ses tests d'exaltation accompagnent le jet
 * @param {Object} [props.request] jet demandé par le MJ : difficulté et jet caché pré-remplis, le jet lui est rattaché
 * @param {Object} [props.resume] jet du chat ({key, record}) rouvert sur son résultat, pour la relance de Volonté
 */
const VtmRollModal = ({isOpen, onClose, character, actions, mode = ROLL_MODES.POOL, initialTraits = [], power = null, request = null, resume = null}) => {
  const lng = currentLanguage();
  const sheet = character.vtm;
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [modifier, setModifier] = useState(0);
  const [impaired, setImpaired] = useState(false);
  const [bloodSurge, setBloodSurge] = useState(false);
  const [difficulty, setDifficulty] = useState('');
  const [hidden, setHidden] = useState(false);
  const [current, setCurrent] = useState(null);
  const [selection, setSelection] = useState([]);
  const [busy, setBusy] = useState(false);
  // Demande du MJ à laquelle ce jet répond (oubliée par « Nouveau jet »)
  const [requestId, setRequestId] = useState(null);
  // Effet du Coup de sang : identifiant de l'animation en cours (rejouée à chaque activation)
  const effects = useHungerEffectsEnabled();
  const [veins, setVeins] = useState(0);

  useEffect(() => {
    if (!veins) return undefined;
    const timer = setTimeout(() => setVeins(0), 2200);
    return () => clearTimeout(timer);
  }, [veins]);

  const changeBloodSurge = (checked) => {
    setBloodSurge(checked);
    if (checked && effects) setVeins(Date.now());
  };

  const resetBuilder = () => {
    setFirst(initialTraits[0] || '');
    setSecond(initialTraits[1] || '');
    setSpecialty('');
    setModifier(0);
    setImpaired(initialTraits[0] ? isRollImpaired(sheet, initialTraits[0]) : false);
    setBloodSurge(false);
    setCurrent(null);
    setSelection([]);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetBuilder();
    setRequestId(request?.id || null);
    if (request) {
      setDifficulty(request.difficulty === null ? '' : String(request.difficulty));
      setHidden(request.hidden);
    }
    if (resume) setCurrent({key: resume.key, record: resume.record, roll: rollFromRecord(resume.record)});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTraits[0], initialTraits[1], request?.id, resume?.key]);

  const newRoll = () => {
    resetBuilder();
    setRequestId(null);
  };

  const changeFirst = (value) => {
    setFirst(value);
    setImpaired(value ? isRollImpaired(sheet, value) : false);
  };

  const traits = [first, second].filter(Boolean);
  const specialties = getSpecialtiesFor(sheet, traits);
  const isFrenzy = mode === ROLL_MODES.FRENZY;
  const isActivate = mode === ROLL_MODES.ACTIVATE && Boolean(power);
  const powerName = power ? localized(power.name, lng) : '';
  const powerRouseCount = power?.rouse || 0;
  const title = (power && powerName) || (isFrenzy ? vtmT('frenzyCheck') : vtmT('roll'));
  const poolSize = isFrenzy
    ? frenzyPoolSize({willpower: getRemaining(sheet.willpower, getWillpowerMax(sheet)), humanity: sheet.humanity})
    : buildPool({
      base: getBaseParts(sheet, traits, lng),
      specialty: specialty || undefined,
      bloodSurge: bloodSurge ? sheet.bloodPotency : undefined,
      impaired,
      modifiers: [{label: 'modifier', value: modifier}],
    }).size;
  const hungerCount = isFrenzy ? 0 : splitPool(poolSize, sheet.hunger).hunger;
  const disciplines = sortDisciplineEntries(sheet.disciplines, lng).filter(([, value]) => value.level > 0);

  const launch = async () => {
    setBusy(true);
    try {
      if (isActivate) {
        const result = await actions.activatePower({power, label: powerName, hidden});
        setCurrent({...result, activated: true});
      } else if (isFrenzy) {
        const result = await actions.rollFrenzy({difficulty: parseDifficulty(difficulty), hidden, requestId});
        setCurrent({...result, frenzy: true});
      } else {
        const traitsLabel = getRollLabel(traits, lng);
        setCurrent(await actions.rollDicePool({
          base: getBaseParts(sheet, traits, lng),
          label: power ? `${powerName} · ${traitsLabel}` : traitsLabel,
          power,
          specialty: specialty || undefined,
          bloodSurge,
          impaired,
          modifier,
          difficulty: parseDifficulty(difficulty),
          hidden,
          requestId,
        }));
      }
      setSelection([]);
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  const toggleDie = (index) => {
    setSelection(previous => {
      if (previous.includes(index)) return previous.filter(item => item !== index);
      return previous.length < MAX_WILLPOWER_REROLL ? [...previous, index] : previous;
    });
  };

  const reroll = async () => {
    setBusy(true);
    try {
      setCurrent(await actions.rerollWithWillpower(current, selection));
      setSelection([]);
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  const canReroll = current && !current.frenzy && !current.activated && !current.record.willpowerRerolled && current.record.regular.length > 0;

  return (
    <>
    {isOpen && veins > 0 && <VtmBloodSurgeVeins key={veins} />}
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={`vtmModal${effects && bloodSurge && !current ? ' surging' : ''}`}
      overlayClassName="vtmModalOverlay"
      contentLabel={title}
    >
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{title}</h2>
        <span className="vtmModalSubtitle">{character.name}</span>
      </div>
      {requestId && !current && <p className="vtmRequestNote">{vtmT('rollRequestFromDm')}</p>}

      {!current && (
        <form className="vtmRollForm" onSubmit={event => { event.preventDefault(); launch(); }}>
          {isActivate && (
            <>
              <p className="vtmHint">{vtmT('activatePowerHint', {count: powerRouseCount})}</p>
              <div className="vtmToggles">
                <Toggle id="vtmActivateHidden" checked={hidden} onChange={setHidden}>{vtmT('hiddenRoll')}</Toggle>
              </div>
            </>
          )}

          {!isFrenzy && !isActivate && (
            <>
              <div className="vtmFieldRow">
                <Field label={vtmT('firstTrait')} htmlFor="vtmFirstTrait">
                  <select id="vtmFirstTrait" value={first} onChange={event => changeFirst(event.target.value)}>
                    <option value="">{vtmT('chooseTrait')}</option>
                    {ATTRIBUTES.map(item => (
                      <option key={item.id} value={item.id}>{localized(item.name, lng)} ({sheet.attributes[item.id]})</option>
                    ))}
                  </select>
                </Field>
                <span className="vtmPlus" aria-hidden="true">+</span>
                <Field label={vtmT('secondTrait')} htmlFor="vtmSecondTrait">
                  <select id="vtmSecondTrait" value={second} onChange={event => { setSecond(event.target.value); setSpecialty(''); }}>
                    <option value="">{vtmT('none')}</option>
                    <optgroup label={vtmT('skills')}>
                      {SKILLS.map(item => (
                        <option key={item.id} value={item.id}>{localized(item.name, lng)} ({sheet.skills[item.id]})</option>
                      ))}
                    </optgroup>
                    {disciplines.length > 0 && (
                      <optgroup label={vtmT('disciplines')}>
                        {disciplines.map(([id, value]) => (
                          <option key={id} value={id}>{localized(DISCIPLINES_BY_ID[id]?.name, lng)} ({value.level})</option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label={vtmT('attributes')}>
                      {ATTRIBUTES.map(item => (
                        <option key={item.id} value={item.id}>{localized(item.name, lng)} ({sheet.attributes[item.id]})</option>
                      ))}
                    </optgroup>
                  </select>
                </Field>
              </div>

              {specialties.length > 0 && (
                <Field label={vtmT('specialty')} htmlFor="vtmSpecialty">
                  <select id="vtmSpecialty" value={specialty} onChange={event => setSpecialty(event.target.value)}>
                    <option value="">{vtmT('none')}</option>
                    {specialties.map(item => <option key={item.name} value={item.name}>{item.name} (+1)</option>)}
                  </select>
                </Field>
              )}

              <div className="vtmFieldRow">
                <Field label={vtmT('modifier')} htmlFor="vtmModifier">
                  <div className="vtmStepper">
                    <button type="button" onClick={() => setModifier(modifier - 1)} aria-label="-1">−</button>
                    <output id="vtmModifier">{modifier > 0 ? `+${modifier}` : modifier}</output>
                    <button type="button" onClick={() => setModifier(modifier + 1)} aria-label="+1">+</button>
                  </div>
                </Field>
                <Field label={vtmT('difficulty')} htmlFor="vtmDifficulty">
                  <input id="vtmDifficulty" type="number" min="0" inputMode="numeric" placeholder={vtmT('difficultyUnknown')} value={difficulty} onChange={event => setDifficulty(event.target.value)} />
                </Field>
              </div>

              <div className="vtmToggles">
                <Toggle id="vtmImpaired" checked={impaired} onChange={setImpaired}>{vtmT('impairedPenalty')} (−2)</Toggle>
                <Toggle id="vtmBloodSurge" checked={bloodSurge} onChange={changeBloodSurge}>
                  {vtmT('bloodSurge')} <span className="vtmMutedInline">{vtmT('bloodSurgeHint', {value: bloodSurgeBonus(sheet.bloodPotency)})}</span>
                </Toggle>
                <Toggle id="vtmHidden" checked={hidden} onChange={setHidden}>{vtmT('hiddenRoll')}</Toggle>
              </div>
            </>
          )}

          {isFrenzy && (
            <>
              <Field label={vtmT('difficulty')} htmlFor="vtmFrenzyDifficulty">
                <input id="vtmFrenzyDifficulty" type="number" min="0" inputMode="numeric" placeholder={vtmT('difficultyUnknown')} value={difficulty} onChange={event => setDifficulty(event.target.value)} />
              </Field>
              <div className="vtmToggles">
                <Toggle id="vtmFrenzyHidden" checked={hidden} onChange={setHidden}>{vtmT('hiddenRoll')}</Toggle>
              </div>
            </>
          )}

          <div className="vtmRollSummary">
            <span>
              {!isActivate && vtmT('poolSummary', {size: poolSize, hunger: hungerCount})}
              {powerRouseCount > 0 && (
                <span className="vtmMutedInline">{isActivate ? '' : ' · '}{vtmT('powerRouseCount', {count: powerRouseCount})}</span>
              )}
            </span>
            <button type="submit" className="vtmButton primary" disabled={busy || (!isFrenzy && !isActivate && !first)}>
              {isActivate ? vtmT('activatePower') : vtmT('rollAction')}
            </button>
          </div>
        </form>
      )}

      {current && (
        <div className="vtmRollOutcome">
          {current.activated
            ? <PowerRouseLines checks={current.record.powerRouses} className="vtmRouseLine" />
            : <VtmPoolResult record={current.record} selection={selection} onToggleDie={canReroll ? toggleDie : undefined} />}
          {current.frenzy && current.record.outcome && (
            <p className="vtmFrenzyLine">{current.result.resisted ? vtmT('frenzyResisted') : vtmT('frenzyLost')}</p>
          )}
          <div className="vtmModalActions">
            {canReroll && (
              <button type="button" className="vtmButton" onClick={reroll} disabled={busy || selection.length === 0}>
                {vtmT('willpowerReroll', {count: selection.length})}
              </button>
            )}
            {!current.activated && <button type="button" className="vtmButton ghost" onClick={newRoll}>{vtmT('newRoll')}</button>}
            <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('close')}</button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
};

export default VtmRollModal;
